import React, { useState, useEffect, useCallback } from 'react';
import { Save, Users, BarChart3, FileText } from 'lucide-react';
import {
  salvarContrato, salvarSebraeDoLaboratorio, listarUsoMensal, cnpjDigitos, cnpjComMascara,
} from '../../data/admin';
import { listLabMembers, fetchLabMemberProfiles, atualizarMembro, removeLabMember } from '../../data/labs';
import { MARK } from '../../constants';
import SebraeDoLaboratorio from './SebraeDoLaboratorio';

const TIERS = [
  { v: '',          r: 'Não definido' },
  { v: 'interno',   r: 'Interno (não fatura)' },
  { v: 'piloto',    r: 'Piloto' },
  { v: 'essencial', r: 'Essencial' },
  { v: 'pro',       r: 'Pro' },
];

const PAPEIS = [
  { v: 'admin', r: 'Administrador' }, { v: 'moderator', r: 'Moderador' },
  { v: 'technician', r: 'Técnico' },  { v: 'viewer', r: 'Visualizador' },
];

const Campo = ({ label, children, dica }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {dica && <p className="text-xs text-slate-400 mt-0.5">{dica}</p>}
  </div>
);

const input = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/** Uso contra limite, com selo a partir de 80%. Sem limite, só o número. */
const UsoContra = ({ rotulo, usado, limite }) => {
  const pct = limite ? (usado / limite) * 100 : 0;
  return (
    <div>
      <p className="text-slate-500 text-xs">{rotulo}</p>
      <p className="font-semibold text-slate-900 text-sm">
        {usado}{limite ? ` / ${limite}` : ''}
        {limite && pct >= 100 && <span title="No limite ou acima"> {MARK.BAD}</span>}
        {limite && pct >= 80 && pct < 100 && <span title="Perto do limite"> {MARK.WARN}</span>}
      </p>
      {!limite && <p className="text-xs text-slate-400">sem limite</p>}
    </div>
  );
};

const DetalheLaboratorio = ({ linha, onSalvou }) => {
  const [form, setForm]       = useState({});
  const [mensal, setMensal]   = useState([]);
  const [membros, setMembros] = useState([]);
  const [erro, setErro]       = useState('');
  const [aviso, setAviso]     = useState('');
  const [salvando, setSalvando] = useState(false);

  const labId = linha?.id;

  useEffect(() => {
    setForm({
      cnpj:                   linha?.contrato?.cnpj || '',
      razao_social:           linha?.contrato?.razao_social || '',
      contato_nome:           linha?.contrato?.contato_nome || '',
      contato_email:          linha?.contrato?.contato_email || '',
      contato_telefone:       linha?.contrato?.contato_telefone || '',
      sebrae:                 !!linha?.sebrae,
      sebrae_responsavel:     linha?.sebrae_responsavel || '',
      tier:                   linha?.contrato?.tier || '',
      inicio_em:              linha?.contrato?.inicio_em || '',
      mensalidade:            linha?.contrato?.mensalidade ?? '',
      preco_excedente_sessao: linha?.contrato?.preco_excedente_sessao ?? '',
      limite_membros:         linha?.contrato?.limite_membros ?? '',
      limite_clientes:        linha?.contrato?.limite_clientes ?? '',
      franquia_sessoes_mes:   linha?.contrato?.franquia_sessoes_mes ?? '',
      active:                 linha?.contrato?.active ?? true,
      assistente_ia:          !!linha?.contrato?.assistente_ia,
      expires_at:             linha?.contrato?.expires_at || '',
      notes:                  linha?.contrato?.notes || '',
    });
    setErro(''); setAviso('');
  }, [linha]);

  const carregarUsoEEquipe = useCallback(async () => {
    if (!labId) return;
    try {
      setMensal(await listarUsoMensal(labId) || []);
    } catch (e) { console.error('uso mensal:', e); setMensal([]); }

    try {
      const lista = await listLabMembers(labId) || [];
      const perfis = await fetchLabMemberProfiles(labId);
      setMembros(lista
        .map(m => ({ ...m, perfil: (perfis || []).find(p => p.id === m.user_id) || null }))
        // A conta de plataforma atravessa todos os laboratórios e não é da
        // equipe de nenhum — listá-la aqui somaria uma pessoa a mais.
        .filter(m => !m.perfil?.is_super_admin));
    } catch (e) { console.error('equipe:', e); setMembros([]); }
  }, [labId]);

  useEffect(() => { carregarUsoEEquipe(); }, [carregarUsoEEquipe]);

  const vazioParaNulo = (v) => (v === '' || v === null || v === undefined ? null : v);
  const inteiro = (v) => (v === '' ? null : parseInt(v) || null);

  const salvar = async () => {
    setSalvando(true); setErro(''); setAviso('');
    try {
      await salvarContrato(labId, {
        cnpj:                   cnpjDigitos(form.cnpj) || null,
        razao_social:           vazioParaNulo(form.razao_social?.trim()),
        contato_nome:           vazioParaNulo(form.contato_nome?.trim()),
        contato_email:          vazioParaNulo(form.contato_email?.trim()),
        contato_telefone:       vazioParaNulo(form.contato_telefone?.trim()),
        tier:                   vazioParaNulo(form.tier),
        inicio_em:              vazioParaNulo(form.inicio_em),
        mensalidade:            form.mensalidade === '' ? null : Number(form.mensalidade),
        preco_excedente_sessao: form.preco_excedente_sessao === '' ? null : Number(form.preco_excedente_sessao),
        limite_membros:         inteiro(form.limite_membros),
        limite_clientes:        inteiro(form.limite_clientes),
        franquia_sessoes_mes:   inteiro(form.franquia_sessoes_mes),
        active:                 !!form.active,
        assistente_ia:          !!form.assistente_ia,
        expires_at:             vazioParaNulo(form.expires_at),
        notes:                  vazioParaNulo(form.notes?.trim()),
      });
      await salvarSebraeDoLaboratorio(labId, {
        sebrae:             form.sebrae,
        sebrae_responsavel: vazioParaNulo(form.sebrae_responsavel?.trim()),
      });
      setAviso('Contrato salvo.');
      onSalvou?.();
    } catch (e) {
      // A mensagem do Postgres é escrita para ser lida por quem clicou —
      // mesmo padrão que `atualizar_membro` já estabeleceu.
      setErro(e.message?.includes('cnpj_check')
        ? 'CNPJ precisa ter 14 dígitos.'
        : e.message);
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Desativar não bloqueia na hora, e a tela precisa dizer isso.
   *
   * A licença é um vale assinado guardado no computador do cliente, válido por
   * 7 dias e renovado a cada abertura com internet. Sem este aviso, a operação
   * desativa, vê o cliente trabalhando no dia seguinte e conclui que o botão
   * não funciona.
   *
   * "Até 7 dias" e não uma data exata de propósito: o painel não sabe quando o
   * vale de cada máquina foi emitido — isso vive no IndexedDB do cliente.
   */
  const alternarAtivo = () => {
    if (form.active && !window.confirm(
      `Desativar ${linha.name}?\n\n`
      + 'O bloqueio NÃO é imediato. O laboratório continua funcionando com a '
      + 'liberação que já tem: até 7 dias se estiver sem internet, ou até a '
      + 'próxima vez que abrir o app conectado.')) return;
    setForm(f => ({ ...f, active: !f.active }));
  };

  const mudarPapel = async (m, papel) => {
    try { await atualizarMembro(m.id, { role: papel }); await carregarUsoEEquipe(); }
    catch (e) { setErro(e.message); }
  };

  const tirarMembro = async (m) => {
    const nome = m.perfil?.full_name || m.perfil?.email || 'este membro';
    if (!window.confirm(`Remover ${nome} de ${linha.name}?`)) return;
    try { await removeLabMember(m.id); await carregarUsoEEquipe(); }
    catch (e) { setErro(e.message); }
  };

  if (!linha) return null;

  const sessoesDoMes = mensal[0]?.sessoes ?? 0;

  return (
    <div className="space-y-4">
      {/* ── Contrato ── */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-slate-400" />
            <h3 className="font-medium text-slate-900">{linha.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={alternarAtivo}
              className={`px-3 py-1.5 rounded text-sm border ${
                form.active
                  ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'}`}>
              {form.active ? 'Desativar' : 'Reativar'}
            </button>
            <button onClick={salvar} disabled={salvando}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm
                         hover:bg-blue-700 disabled:opacity-40">
              <Save size={14} /> Salvar
            </button>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Campo label="Razão social">
            <input className={input} value={form.razao_social}
              onChange={(e) => setForm({ ...form, razao_social: e.target.value })} />
          </Campo>
          <Campo label="CNPJ">
            <input className={input} value={cnpjComMascara(form.cnpj)} placeholder="00.000.000/0000-00"
              onChange={(e) => setForm({ ...form, cnpj: cnpjDigitos(e.target.value) })} />
          </Campo>
          <Campo label="Plano">
            <select className={input} value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}>
              {TIERS.map(t => <option key={t.v} value={t.v}>{t.r}</option>)}
            </select>
          </Campo>

          <Campo label="Contato">
            <input className={input} value={form.contato_nome}
              onChange={(e) => setForm({ ...form, contato_nome: e.target.value })} />
          </Campo>
          <Campo label="E-mail">
            <input className={input} type="email" value={form.contato_email}
              onChange={(e) => setForm({ ...form, contato_email: e.target.value })} />
          </Campo>
          <Campo label="Telefone">
            <input className={input} value={form.contato_telefone}
              onChange={(e) => setForm({ ...form, contato_telefone: e.target.value })} />
          </Campo>

          <SebraeDoLaboratorio
            sebrae={form.sebrae} responsavel={form.sebrae_responsavel}
            onSebrae={(v) => setForm({ ...form, sebrae: v })}
            onResponsavel={(v) => setForm({ ...form, sebrae_responsavel: v })} />

          {/* Cada mensagem ao assistente é uma chamada paga ao Claude: liga
              quem opera o produto, nunca o laboratório. */}
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!form.assistente_ia} className="mt-0.5"
              onChange={(e) => setForm({ ...form, assistente_ia: e.target.checked })} />
            <span>
              Assistente de IA
              <span className="block text-xs text-slate-500">Conversa que leva ao modal certo e sugere o próximo passo.</span>
            </span>
          </label>

          <Campo label="Início do contrato">
            <input className={input} type="date" value={form.inicio_em}
              onChange={(e) => setForm({ ...form, inicio_em: e.target.value })} />
          </Campo>
          <Campo label="Vencimento" dica="Vazio = sem vencimento">
            <input className={input} type="date" value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </Campo>
          <Campo label="Mensalidade (R$)">
            <input className={input} type="number" step="0.01" min="0" value={form.mensalidade}
              onChange={(e) => setForm({ ...form, mensalidade: e.target.value })} />
          </Campo>

          <Campo label="Limite de membros" dica="Vazio = sem limite">
            <input className={input} type="number" min="1" value={form.limite_membros}
              onChange={(e) => setForm({ ...form, limite_membros: e.target.value })} />
          </Campo>
          <Campo label="Limite de clientes" dica="Vazio = sem limite">
            <input className={input} type="number" min="1" value={form.limite_clientes}
              onChange={(e) => setForm({ ...form, limite_clientes: e.target.value })} />
          </Campo>
          <Campo label="Franquia de sessões/mês" dica="Acima disto é cobrado, não bloqueado">
            <input className={input} type="number" min="1" value={form.franquia_sessoes_mes}
              onChange={(e) => setForm({ ...form, franquia_sessoes_mes: e.target.value })} />
          </Campo>

          <Campo label="Preço do excedente (R$/sessão)">
            <input className={input} type="number" step="0.01" min="0" value={form.preco_excedente_sessao}
              onChange={(e) => setForm({ ...form, preco_excedente_sessao: e.target.value })} />
          </Campo>
          <div className="sm:col-span-2">
            <Campo label="Anotações">
              <input className={input} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Campo>
          </div>
        </div>

        {(erro || aviso) && (
          <div className="px-4 pb-3">
            <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
              {erro || aviso}
            </div>
          </div>
        )}

        {!form.active && (
          <div className="px-4 pb-3">
            <p className="text-xs text-slate-600">
              {MARK.WARN} Desativado. O bloqueio chega ao cliente na próxima abertura
              com internet, ou em até 7 dias se ele estiver offline.
            </p>
          </div>
        )}
      </div>

      {/* ── Uso ── */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <BarChart3 size={16} className="text-slate-400" />
          <h3 className="font-medium text-slate-900">Uso</h3>
        </div>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-slate-100">
          <UsoContra rotulo="Clientes"   usado={linha.clientes} limite={linha.contrato?.limite_clientes} />
          <UsoContra rotulo="Equipe"     usado={membros.length} limite={linha.contrato?.limite_membros} />
          <UsoContra rotulo="Sessões no mês" usado={sessoesDoMes} limite={linha.contrato?.franquia_sessoes_mes} />
          <div>
            <p className="text-slate-500 text-xs">Embriões</p>
            <p className="font-semibold text-slate-900 text-sm">{linha.embrioes}</p>
          </div>
        </div>

        {mensal.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500 text-center">Nenhum lançamento ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Mês</th>
                  <th className="px-3 py-2 text-center font-medium">OPU</th>
                  <th className="px-3 py-2 text-center font-medium">FIV</th>
                  <th className="px-3 py-2 text-center font-medium">TE</th>
                  <th className="px-3 py-2 text-center font-medium">DG</th>
                  <th className="px-3 py-2 text-center font-medium">Protoc.</th>
                  <th className="px-3 py-2 text-center font-medium">Embriões</th>
                  <th className="px-3 py-2 text-center font-medium">Sessões</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mensal.map(m => {
                  const fr = linha.contrato?.franquia_sessoes_mes;
                  const acima = fr && m.sessoes > fr;
                  return (
                    <tr key={m.mes}>
                      <td className="px-4 py-1.5 text-slate-700">
                        {new Date(`${m.mes}T00:00:00`).toLocaleDateString('pt-BR',
                          { month: 'long', year: 'numeric' })}
                      </td>
                      <td className="px-3 py-1.5 text-center">{m.opu || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{m.fiv || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{m.te || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{m.dg || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{m.protocolo || '—'}</td>
                      <td className="px-3 py-1.5 text-center">{m.embrioes || '—'}</td>
                      <td className="px-3 py-1.5 text-center font-medium text-slate-900">
                        {m.sessoes}
                        {acima && <span title={`Acima da franquia de ${fr}`}> {MARK.WARN}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Equipe ── */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Users size={16} className="text-slate-400" />
          <h3 className="font-medium text-slate-900">Equipe</h3>
          {membros.length === 0 && (
            <span className="text-xs text-slate-600">
              {MARK.WARN} nenhum membro — ninguém consegue entrar neste laboratório
            </span>
          )}
        </div>
        {membros.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {membros.map(m => {
                  const ehDono = m.user_id === linha.owner_id;
                  return (
                    <tr key={m.id}>
                      <td className="px-4 py-2">
                        <span className="font-medium text-slate-900">
                          {m.perfil?.full_name || 'Nome não disponível'}
                        </span>
                        {ehDono && <span className="text-xs text-slate-500"> · dono</span>}
                        <span className="block text-xs text-slate-500">{m.perfil?.email || '—'}</span>
                      </td>
                      <td className="px-3 py-2 w-44">
                        <select className={input} value={m.role} disabled={ehDono}
                          onChange={(e) => mudarPapel(m, e.target.value)}>
                          {PAPEIS.map(p => <option key={p.v} value={p.v}>{p.r}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs w-24">
                        {m.status === 'active' ? 'ativo' : 'inativo'}
                      </td>
                      <td className="px-3 py-2 text-right w-24">
                        {!ehDono && (
                          <button onClick={() => tirarMembro(m)}
                            className="text-slate-500 hover:text-slate-900 text-xs px-2 py-1 rounded hover:bg-slate-100">
                            Remover
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalheLaboratorio;
